import React, { useCallback, useEffect, useState } from "react";
import api, { describeError } from "@/lib/api";
import { Panel, Skeleton, ErrorState, Empty, Pager, Badge, day, dayTime, selectClass } from "@/components/platform/ui";

const BLANK = { search: "", role: "", start: "", end: "", last_login_after: "" };

export default function PlatformUsers() {
  const [f, setF] = useState(BLANK);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    const params = { page, page_size: pageSize };
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/platform/users", { params });
      setData(data);
    } catch (e) {
      setErr(describeError(e, "Could not load users."));
    } finally {
      setLoading(false);
    }
  }, [f, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => { setPage(1); setF((p) => ({ ...p, [k]: e.target.value })); };

  return (
    <div data-testid="owner-users" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl tracking-tight text-navy sm:text-4xl">Users</h1>
        <p className="mt-1 text-sm text-muted-foreground">Every account across all organisations.</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <input value={f.search} onChange={set("search")} placeholder="Search name or email"
               data-testid="user-search-input" className={`${selectClass} lg:col-span-2`} />
        <select value={f.role} onChange={set("role")} data-testid="user-role-filter" className={selectClass}>
          <option value="">All roles</option>
          {["firm_admin", "advocate", "staff", "client", "platform_owner"].map((r) => (
            <option key={r} value={r}>{r.replace("_", " ")}</option>
          ))}
        </select>
        <select value={pageSize} data-testid="user-page-size" className={selectClass}
                onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}>
          {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
        </select>
        <input type="date" value={f.start} onChange={set("start")} data-testid="user-start-filter" className={selectClass} />
        <input type="date" value={f.end} onChange={set("end")} data-testid="user-end-filter" className={selectClass} />
        <label className="flex flex-col text-[11px] text-muted-foreground lg:col-span-2">
          Signed in since
          <input type="date" value={f.last_login_after} onChange={set("last_login_after")}
                 data-testid="user-lastlogin-filter" className={selectClass} />
        </label>
        <button type="button" onClick={() => { setF(BLANK); setPage(1); }} data-testid="user-clear-filters"
                className="h-10 self-end rounded-md border border-border bg-white px-3 text-sm text-navy hover:bg-secondary">
          Clear
        </button>
      </div>

      <Panel title="All users" testid="owner-users-panel">
        {loading ? <Skeleton rows={6} testid="user-loading" />
          : err ? <ErrorState message={err} onRetry={load} testid="user-error" />
          : !data.items.length ? <Empty message="No users match these filters." testid="user-empty" />
          : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm" data-testid="user-table">
                  <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      {["Name", "Email", "Role", "Organization", "Registered", "Last sign-in"].map((h) => (
                        <th key={h} className="px-5 py-3 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.items.map((u) => (
                      <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-secondary/40">
                        <td className="px-5 py-3 font-medium text-navy">{u.name}</td>
                        <td className="px-5 py-3 text-muted-foreground">{u.email}</td>
                        <td className="px-5 py-3"><Badge>{(u.role || "").replace("_", " ")}</Badge></td>
                        <td className="px-5 py-3 text-xs text-muted-foreground">
                          {u.organization_id || "— platform —"}
                        </td>
                        <td className="px-5 py-3 text-muted-foreground">{day(u.created_at)}</td>
                        <td className="px-5 py-3 text-muted-foreground">{dayTime(u.last_login_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} testid="user-pager" />
            </>
          )}
      </Panel>
    </div>
  );
}
